import { faCamera } from "@fortawesome/pro-regular-svg-icons";
import { faX } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notification } from "antd";
import React, { useEffect } from "react";
import { globalAppConfig } from "../../config";
import GlobalStoreContext from "../../context/GlobalStoreContext";
import Helper from "../../Helper";
import { CalendarEvent } from "../../models/CalendarEvent";
import PlotAPI from "../../plotAPI";

interface DamnPhotoSectionProps {
    calendarEvent: CalendarEvent;
}
const DamnPhotoSection: React.FC<DamnPhotoSectionProps> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const inputRef = React.useRef<HTMLInputElement>(null);

    const [calendarEventAttachments, setCalendarEventAttachments] = React.useState<any[]>([]);

    useEffect(() => {
        updateCalendarEventAttachments();
    }, [props.calendarEvent, context.currentProjectInfo]);

    const updateCalendarEventAttachments = () => {
        if (!props.calendarEvent || !context.currentProjectInfo) {
            setCalendarEventAttachments([]);
            return;
        }

        PlotAPI.getCalendarEventAttachments(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            props.calendarEvent
        ).then((attachments) => {
            attachments = attachments.map((file: any) => {
                const baseUrl = fileUrl(file);
                return {
                    ...file,
                    thumbUrl: file.content_type.startsWith("image") ? baseUrl + "/picture" : undefined,
                    uid: file.id,
                    name: file.filename,
                    downloadUrl: baseUrl + "/download",
                    fileUrl: baseUrl + "/show_file",
                    contentType: file.content_type,
                };
            });

            setCalendarEventAttachments(attachments);
        });
    };

    const fileUrl = (file: any) => {
        const projectToken = context.currentProjectInfo?.unique_token;
        const claimedByTeamSubdomain = context.currentProjectInfo?.claimed_by_team_subdomain;
        return (
            `${Helper.baseUrlAltSubdomain(claimedByTeamSubdomain)}/projects/${projectToken}/calendar_events/${props.calendarEvent.id}/attachments/` +
            file.id
        );
    };

    const onFileUpload = (file: React.ChangeEvent<HTMLInputElement, HTMLInputElement>) => {
        if (!file.target.files || file.target.files.length === 0) {
            return;
        }

        const file_data = file.target.files[0];
        const formData = new FormData();
        formData.append("File", file_data);

        if (!context.currentProjectInfo || !props.calendarEvent.id) {
            return;
        }

        PlotAPI.createCalendarEventAttachment(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            props.calendarEvent,
            formData
        ).then((response) => {
            if (response) {
                notification.success({
                    message: "File uploaded and attached to delivery",
                });

                updateCalendarEventAttachments();
            } else {
                notification.error({
                    message: "Error uploading file",
                });
            }
        });
    };

    const removeImage = (index: number) => {
        if (
            !context.currentProjectInfo ||
            !props.calendarEvent.id ||
            !calendarEventAttachments[index] ||
            !calendarEventAttachments[index].id
        ) {
            return;
        }

        PlotAPI.deleteCalendarEventAttachment(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            props.calendarEvent,
            calendarEventAttachments[index].id
        ).then((response) => {
            if (response) {
                notification.success({
                    message: "Attachment removed from delivery",
                });

                updateCalendarEventAttachments();
            } else {
                notification.error({
                    message: "Error removing attachment",
                });
            }
        });
    };

    return (
        <section className="mx-4 mt-4">
            <div className="bg-white rounded-2xl shadow-xl p-5 border border-gray-200/50">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faCamera} className="text-plot-orange" />
                    Material Photos
                </h3>
                <button
                    type="button"
                    className="w-full cursor-pointer border-3 border-dashed border-gray-300 hover:border-plot-orange rounded-xl p-10 transition-all duration-200 group hover:bg-linear-to-br hover:from-plot-orange/5 hover:to-plot-orange/10"
                    onClick={() => {
                        inputRef.current?.click();
                    }}
                >
                    <FontAwesomeIcon
                        icon={faCamera}
                        size="4x"
                        className="mx-auto mb-3 text-gray-400 group-hover:text-plot-orange transition-colors"
                    />

                    <p className="text-sm font-bold text-gray-600 group-hover:text-plot-orange transition-colors">
                        + Add Photos
                    </p>
                </button>
                <input
                    id="damn-photo-upload-input"
                    type="file"
                    ref={inputRef}
                    onChange={(file) => onFileUpload(file)}
                    accept={globalAppConfig.ACCEPTED_UPLOAD_FILE_TYPES}
                    className="hidden"
                    //capture="environment"
                />
                {calendarEventAttachments.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 gap-3">
                        {calendarEventAttachments.map((image, index) => (
                            <div key={image.id} className="relative group">
                                <img
                                    src={image.fileUrl}
                                    alt={`Upload ${index + 1}`}
                                    className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                                />
                                <button
                                    onClick={() => removeImage(index)}
                                    className="cursor-pointer absolute -top-2 -right-2 bg-red-500  hover:bg-red-600 text-white rounded-full p-1 shadow-lg  transition-all"
                                    aria-label="Remove image"
                                >
                                    <FontAwesomeIcon icon={faX} className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default DamnPhotoSection;
